/**
 * Grid maths shared by the KPI row and its skeleton.
 *
 * Lives apart from both so the two can use it without importing each other —
 * the skeleton needs the column rule to match the real row, and the real row
 * needs the skeleton for its own loading state, which is a cycle if the rule
 * lives in either file.
 */

/**
 * How many columns to lay `count` tiles out in, so every row holds the SAME
 * number.
 *
 * `auto-fit` fills each row and lets the last one come up short — 14 tiles at
 * ~8 per row gave a row of 8 and a row of 6 with two gaps, which reads as a
 * missing tile rather than a wrapped one. Constraining the count to a DIVISOR
 * of the total makes every row full by construction: 14 -> 7 + 7, 9 -> 9, and
 * on a narrower viewport 9 -> 3 + 3 + 3.
 *
 * `target` is the column count the breakpoint would like; the closest divisor
 * to it wins, preferring fewer columns on a tie (wider tiles read better than
 * narrower ones at the same row count).
 */
export function balancedColumns(count, target) {
  if (!count) return 1;
  const divisors = Array.from({ length: count }, (_, i) => i + 1).filter((d) => count % d === 0);
  return divisors.reduce(
    (best, d) => (Math.abs(d - target) < Math.abs(best - target) ? d : best),
    divisors[0]
  );
}

/** Desktop column count for the approved two-row Products Only layout. */
export function twoRowColumns(count) {
  return Math.ceil(count / 2) || 1;
}

/**
 * Desktop grid columns for a two-row layout. Each card spans two tracks so a
 * shorter odd-count row can be offset by one track while retaining the same
 * card width as the first row.
 */
export function twoRowGridTemplateColumns(count) {
  return `repeat(${twoRowColumns(count) * 2}, minmax(0, 1fr))`;
}

/**
 * Explicit desktop placement for a two-row layout. For 9 cards this produces
 * first-row starts 1,3,5,7,9 and second-row starts 2,4,6,8, centering the
 * shorter row with equal whitespace on both sides. Even counts remain full.
 */
export function twoRowGridColumn(count, index) {
  const columns = twoRowColumns(count);
  if (index < columns) return `${1 + index * 2} / span 2`;
  const offset = count % 2 === 1 ? 2 : 1;
  return `${offset + (index - columns) * 2} / span 2`;
}

/** The responsive `gridTemplateColumns` for `count` balanced tiles. */
export const balancedGridColumns = (count, { lgMax, twoRows = false } = {}) => ({
  xs: `repeat(${balancedColumns(count, 2)}, minmax(0, 1fr))`,
  sm: `repeat(${balancedColumns(count, 3)}, minmax(0, 1fr))`,
  md: `repeat(${balancedColumns(count, 5)}, minmax(0, 1fr))`,
  lg: twoRows
    ? twoRowGridTemplateColumns(count)
    : `repeat(${Math.min(balancedColumns(count, 7), lgMax ?? Infinity)}, minmax(0, 1fr))`,
});
